import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { USER_ROLES_BY_NAME } from 'src/constants/userSetting';

/** The flags the sidebar checks before showing a destination; the palette must agree with it. */

/**
 * Nav and actions live in the palette alongside entity results, so it doubles as "how do I get to
 * Reporting" without ever running a query. That is what makes it a command palette rather than a
 * search box.
 *
 * Every private destination in src/configs/routes.js appears here, under the name a person would
 * type rather than the name the route was given. commands.test.ts fails when a new route ships
 * without an entry, which is what keeps the two in step without generating unreadable labels.
 *
 * Seven routes are deliberately absent, because a typed navigation cannot supply what they read on
 * mount. /bills_files and /addendums_files read `history.state` without a guard and throw on
 * arrival. /ar_report reads `propertyId` from the query string and asks for a report on whatever
 * it finds, so opening it bare requests one for null. /allocation_tables reads
 * `allocationCycleId` and `propertyId` the same way and destructures the empty response.
 * /allocations/allocations_charges mounts ReccuringChargesTable with no props, so it asks for the
 * recurring transactions of an undefined resident. Those five are reached from a property or a
 * resident. /auto_billing_credentials renders hard-coded sample data, has no fetch, and nothing
 * in the app links to it, and /notifications renders a static 2012 fixture with no fetch behind
 * it. Both can be added once they are wired to an API.
 */
export const COMMANDS = [{
  id: 'dashboard',
  label: 'Dashboard',
  section: 'Navigation',
  path: '/active_dashboard',
  // The sidebar's Properties entry lands here, and customers find residents and billing in its tabs.
  keywords: 'home overview billing module properties residents allocations charges'
}, {
  id: 'properties',
  label: 'Properties',
  section: 'Navigation',
  path: '/new_properties',
  keywords: 'buildings units portfolio',
  // The older management page; customers reach properties through the dashboard, as the sidebar does.
  adminOnly: true
}, {
  id: 'add-property',
  label: 'Add a Property',
  section: 'Actions',
  path: '/new_property',
  keywords: 'new create building',
  requires: 'canAddProperty'
}, {
  id: 'residents',
  label: 'Residents',
  section: 'Navigation',
  path: '/residencies',
  keywords: 'tenants people leases',
  // Reached only from the older properties page, so staff only, like it.
  adminOnly: true
}, {
  id: 'bills',
  label: 'Utility Bills',
  section: 'Navigation',
  path: '/bills',
  keywords: 'invoices utilities usage',
  requires: 'billsDashboard'
}, {
  id: 'allocations',
  label: 'Allocations',
  section: 'Navigation',
  path: '/allocations',
  keywords: 'charges history cycles',
  adminOnly: true
}, {
  id: 'allocation-history',
  label: 'Allocation History',
  section: 'Navigation',
  path: '/allocations/allocations_history',
  keywords: 'allocations past cycles runs',
  adminOnly: true
}, {
  id: 'reporting',
  label: 'Reporting',
  section: 'Navigation',
  path: '/reporting',
  keywords: 'reimbursement recovery export receipts',
  requires: 'reports'
}, {
  id: 'payments',
  label: 'Payments',
  section: 'Navigation',
  path: '/payments',
  keywords: 'transactions ledger refunds',
  requires: 'notDemoMode'
}, {
  id: 'subscriptions',
  label: 'Payments & Subscriptions',
  section: 'Navigation',
  path: '/payments_and_subscriptions',
  keywords: 'plan card billing invoice',
  requires: 'newPaymentsAndSubscriptions'
}, {
  id: 'pricing',
  label: 'Pro Pricing',
  section: 'Navigation',
  path: '/pro-pricing',
  keywords: 'plans upgrade cost',
  requires: 'stripeInvoicing',
  // In the sidebar's staff block, so staff only here too.
  adminOnly: true
}, {
  id: 'files',
  label: 'File Cabinet',
  section: 'Navigation',
  path: '/file_cabinet',
  keywords: 'documents uploads storage'
}, {
  id: 'appfolio',
  label: 'AppFolio',
  section: 'Navigation',
  path: '/appfolio',
  keywords: 'pms integration sync',
  adminOnly: true
}, {
  id: 'settings',
  label: 'Profile Settings',
  section: 'Navigation',
  path: '/profile-settings',
  keywords: 'account password profile me',
  requires: 'notDemoMode'
}, {
  id: 'team',
  label: 'Team Settings',
  section: 'Navigation',
  path: '/team_settings',
  keywords: 'users invite permissions colleagues',
  // TeamSettingsPage redirects a demo session to / on mount, so an ungated entry opens nothing.
  requires: 'notDemoMode'
}, {
  id: 'fees',
  label: 'Fee Structure',
  section: 'Navigation',
  path: '/fee-structure',
  keywords: 'settings charges markup',
  requires: 'canUseFeeStructure'
}, {
  id: 'livy',
  label: 'Ask Livy',
  section: 'Actions',
  path: '/livy',
  keywords: 'assistant ai chat help',
  drawer: 'ask'
},
// Its own route rather than a tab, and staff-only inside the page, so it is reachable by name
// here the same way /admin is.
{
  id: 'bill-queue',
  label: 'Bill queue',
  section: 'Navigation',
  path: '/bill-queue',
  keywords: 'bills queue operator work',
  adminOnly: true
}, {
  id: 'kb',
  label: 'Knowledge Base',
  section: 'Help',
  path: '/livy/kb',
  keywords: 'articles how to guide docs',
  drawer: 'help'
},
// The staff support queue; customers ask for help through the drawer.
{
  id: 'support',
  label: 'Support',
  section: 'Help',
  path: '/livy/support',
  keywords: 'ticket help contact issue',
  adminOnly: true
}, {
  id: 'admin',
  label: 'Admin',
  section: 'Navigation',
  path: '/admin',
  keywords: 'companies users',
  adminOnly: true
}, {
  id: 'switch-company',
  label: 'Switch Company',
  section: 'Actions',
  path: '/admin',
  keywords: 'impersonate sudo change tenant',
  adminOnly: true
}, {
  id: 'banners',
  label: 'Information Banners',
  section: 'Navigation',
  path: '/information_banners',
  keywords: 'announcement notice',
  adminOnly: true
}, {
  id: 'livy-admin',
  label: 'Livy Activity',
  section: 'Navigation',
  path: '/livy/admin',
  keywords: 'assistant logs tools limits requests',
  adminOnly: true
}];
const ADMIN_ROLES = [USER_ROLES_BY_NAME.LivableAdmin, USER_ROLES_BY_NAME.LivableSuperAdmin];
export const isAdminRole = role => ADMIN_ROLES.includes(String(role));

/**
 * Under two characters the palette is pure navigation, no query is run at all. Matching is on
 * label and keywords so "recovery" finds Reporting.
 */
export function filterCommands(query, role) {
  let gates = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  // Same gates the sidebar applies: a destination it hides is one the palette must not open either.
  const staff = isAdminRole(role);
  const visible = COMMANDS.filter(c => (!c.adminOnly || staff) && (!c.requires || Boolean(gates[c.requires]))).map(
  // Staff open the full /livy pages; everyone else gets the drawer, since those pages refuse them.
  c => c.drawer && !staff ? _objectSpread(_objectSpread({}, c), {}, {
    path: undefined
  }) : c);
  const q = query.trim().toLowerCase();
  if (!q) return visible;
  return visible.filter(c => {
    var _c$keywords$includes, _c$keywords;
    return c.label.toLowerCase().includes(q) || c.section.toLowerCase().includes(q) || ((_c$keywords$includes = (_c$keywords = c.keywords) === null || _c$keywords === void 0 ? void 0 : _c$keywords.includes(q)) !== null && _c$keywords$includes !== void 0 ? _c$keywords$includes : false);
  });
}
export const SEARCH_MIN_CHARS = 2;
export const shouldSearch = query => query.trim().length >= SEARCH_MIN_CHARS;

/** One entity row, already shaped and given a destination by the proxy. */

/**
 * A destination has to be a path inside this app. The proxy builds these from record ids, but the
 * value still crosses a network boundary and is handed straight to navigate() — and `//host` or
 * `https://host` would navigate off-site rather than open a record.
 */
export const isInAppPath = href => {
  if (typeof href !== 'string') return false;
  const path = href.trim();
  // A backslash is normalised to a path separator while resolving, and URL parsing strips tab, CR
  // and LF outright, so '/\evil.example/x' and '/\n/evil.example/x' both become a network-path
  // reference and leave the site. Rejected raw rather than reasoning about what the browser folds.
  if (/[\\\u0000-\u001f\u007f]/.test(path)) return false;
  return /^\/(?!\/)/.test(path);
};

/**
 * Group names are the proxy's; the palette renders them as section headings. The value is
 * optional because the keys are whatever the proxy sends, and a lookup that can miss should say
 * so rather than leave every caller compensating for a type that claims it cannot.
 */
const SECTION_BY_GROUP = {
  properties: 'Properties',
  units: 'Units',
  residents: 'Residents',
  companies: 'Companies'
};

/**
 * The proxy resolves a destination for every row it returns, so this drops nothing in the normal
 * case. It stays as a guard because a result that cannot be opened is worse than no result, and the
 * count is reported rather than swallowed: silently dropping every row is what made a working
 * search look like an empty one.
 */
/**
 * Section headings for the groups the proxy reported as cut short, so a list can say "at least
 * this many" instead of presenting the best matches as all of them.
 */
export function truncatedSections(truncated) {
  if (!Array.isArray(truncated)) return [];
  return truncated.map(group => SECTION_BY_GROUP[String(group)]).filter(section => section !== undefined);
}
export function flattenSearchResults(results) {
  if (!results || typeof results !== 'object' || Array.isArray(results)) return {
    hits: [],
    dropped: 0
  };
  const hits = [];
  let dropped = 0;
  for (const _ref of Object.entries(results)) {
    var _ref2 = _slicedToArray(_ref, 2);
    const group = _ref2[0];
    const rows = _ref2[1];
    if (!Array.isArray(rows) || !SECTION_BY_GROUP[group]) continue;
    for (const raw of rows) {
      const row = raw;
      // Types checked, not just truthiness: a malformed row with a non-string title reached
      // rankItems' toLowerCase() and took the whole palette down with it.
      if (!row || typeof row !== 'object' || typeof row.title !== 'string' || typeof row.href !== 'string') {
        dropped += 1;
        continue;
      }
      if (!row.title.trim() || !isInAppPath(row.href)) {
        dropped += 1;
        continue;
      }
      // A non-string subtitle is dropped rather than dropping the row: it is forwarded as
      // `keywords`, where rankItems calls toLowerCase() on it and takes the whole palette down.
      const subtitle = typeof row.subtitle === 'string' ? row.subtitle : undefined;
      hits.push(_objectSpread(_objectSpread({}, row), {}, {
        type: group,
        subtitle
      }));
    }
  }
  return {
    hits,
    dropped
  };
}

/** Deduped so the same record arriving twice renders once. */
export function dedupe(items) {
  const seen = new Set();
  return items.filter(i => {
    var _i$id, _i$subtitle;
    // Record identity first: two companies can share a display name, and keying on the name alone
    // dropped the second one even though it opens somewhere else. Name is the fallback for a row
    // that carries no id.
    const key = "".concat(i.type, ":").concat((_i$id = i.id) !== null && _i$id !== void 0 ? _i$id : "".concat(i.title, "|").concat((_i$subtitle = i.subtitle) !== null && _i$subtitle !== void 0 ? _i$subtitle : ''));
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Entity results are appended to the navigation matches rather than shown in a second pane, so
 * ArrowDown walks one list and Enter always means "open the highlighted thing".
 */
export function toCommands(hits) {
  return dedupe(hits).map((h, i) => {
    var _h$id, _SECTION_BY_GROUP$h$t;
    return {
      id: "hit-".concat(i, "-").concat(h.type, "-").concat((_h$id = h.id) !== null && _h$id !== void 0 ? _h$id : h.title),
      label: h.title,
      section: (_SECTION_BY_GROUP$h$t = SECTION_BY_GROUP[h.type]) !== null && _SECTION_BY_GROUP$h$t !== void 0 ? _SECTION_BY_GROUP$h$t : 'Navigation',
      path: h.href,
      keywords: h.subtitle
    };
  });
}

/**
 * Knowledge-base hits render beside entity hits, because "how do I close a cycle" and "where is
 * Maple Court" are the same keystroke to the person typing.
 */
export function toKnowledgeCommands() {
  let articles = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  let staff = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return articles.slice(0, 5).map(a => _objectSpread(_objectSpread({
    id: "kb-".concat(a.slug),
    label: a.title,
    section: 'Help'
  }, staff ? {
    path: "/livy/kb?article=".concat(encodeURIComponent(a.slug))
  } : {
    drawer: 'help',
    topic: a.title,
    article: a.slug
  }), {}, {
    keywords: 'knowledge base help article'
  }));
}

/**
 * One ranked list, rather than navigation then entities then articles in fixed blocks.
 *
 * Grouping by source meant a weak navigation match outranked an exact property name simply because
 * navigation was rendered first, which reads as the search ignoring what was typed.
 */
export function rankItems(items, query) {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  const score = c => {
    var _c$keywords2;
    const label = c.label.toLowerCase();
    if (label === q) return 100;
    if (label.startsWith(q)) return 80;
    if (label.includes(q)) return 60;
    if (((_c$keywords2 = c.keywords) !== null && _c$keywords2 !== void 0 ? _c$keywords2 : '').toLowerCase().includes(q)) return 30;
    return 10;
  };

  // Stable within a score, so equal matches keep the order their source produced.
  return items.map((c, i) => ({
    c,
    i,
    s: score(c)
  })).sort((a, b) => b.s - a.s || a.i - b.i).map(_ref3 => {
    let c = _ref3.c;
    return c;
  });
}

/** Where in a label the query matched, so the palette can emphasise it. */
export function matchRange(label, query) {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  const at = label.toLowerCase().indexOf(q);
  return at === -1 ? null : [at, at + q.length];
}
const RECENTS_KEY = 'livy_palette_recents';
const RECENTS_MAX = 5;

/**
 * An empty palette offering the last few places this person went beats the same static list every
 * time. Stored per browser: it is a convenience, not state worth a round trip.
 */
export function readRecents() {
  try {
    var _localStorage$getItem;
    const raw = JSON.parse((_localStorage$getItem = localStorage.getItem(RECENTS_KEY)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : '[]');
    return Array.isArray(raw) ? raw.filter(v => typeof v === 'string').slice(0, RECENTS_MAX) : [];
  } catch (_unused) {
    return [];
  }
}

/**
 * Only navigation and actions are remembered. An entity or article row's id is generated per
 * response, so it could never be resolved again from an empty palette — remembering them would
 * quietly evict every usable entry and leave no Recent section at all.
 */
export const isRememberable = id => COMMANDS.some(c => c.id === id);
export function rememberRecent(id) {
  if (!isRememberable(id)) return;
  try {
    const next = [id, ...readRecents().filter(v => v !== id)].slice(0, RECENTS_MAX);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch (_unused2) {
    /* a full or blocked localStorage must not break navigation */
  }
}

/**
 * Recents get their own section at the top rather than being floated within the list.
 *
 * Floating them was ineffective: the list is grouped by section for rendering, so a recent
 * Navigation entry still sat behind every Action. A row moves into "Recent" rather than being
 * copied, so nothing renders twice and the list stays complete.
 */
export function withRecentsSection(items, recents) {
  if (!recents.length) return items;
  const byId = new Map(items.map(c => [c.id, c]));
  const recent = recents.map(id => byId.get(id)).filter(c => Boolean(c));
  if (!recent.length) return items;
  const promoted = new Set(recent.map(c => c.id));
  return [...recent.map(c => _objectSpread(_objectSpread({}, c), {}, {
    section: 'Recent'
  })), ...items.filter(c => !promoted.has(c.id))];
}

/**
 * One heading per section, ordered by that section's best-ranked member.
 *
 * The index each row carries is its position in the *grouped* order, not the ranked order, because
 * that grouped order is what ends up on screen. Numbering by the ranked order instead meant
 * ArrowDown skipped a visually adjacent row and later jumped backward, taking scrollIntoView with it.
 *
 * Grouping consecutive runs instead of collecting by name rendered a section's heading twice
 * whenever its members straddled another section's score, which reads as duplicate results.
 */
export function groupBySection(items) {
  const bySection = new Map();
  for (const cmd of items) {
    const rows = bySection.get(cmd.section);
    if (rows) rows.push(cmd);else bySection.set(cmd.section, [cmd]);
  }
  let index = 0;
  return [...bySection.entries()].map(_ref4 => {
    let _ref5 = _slicedToArray(_ref4, 2),
      label = _ref5[0],
      rows = _ref5[1];
    return {
      label,
      items: rows.map(cmd => ({
        cmd,
        index: index++
      }))
    };
  });
}

/** The rows in the order they are rendered, which is the order the keyboard must walk. */
export function inDisplayOrder(items) {
  return groupBySection(items).flatMap(section => section.items.map(row => row.cmd));
}